// FLOW Platform — FHT game module.
//
// Ports the playable mechanics from fht_v1.html with three substantive
// changes for production:
//
//   1. Validation hits the embedded compound graph (compound-graph.js),
//      not a per-puzzle answer key. The server's /today response has
//      no answer field; this module never asks for one.
//   2. Hints (ONE letter a puzzle since 2.14.29; the whole-word hint left in 2.14.28)
//      compute their target via a
//      graph search rather than reading from the puzzle JSON. The
//      first valid completion the search finds is treated as the
//      "canonical" for hint purposes. Players are still credited for
//      solving with any valid alternate bridge.
//   3. In-progress state persists in localStorage keyed by puzzle_id
//      (flow_fht_state:<id>). Closing the tab mid-puzzle is safe.
//
// On puzzle end the module calls `onComplete(state)` with the final
// state. The shell handles the network submission (POST /plays) and
// renders the post-game screen.
//
// Play model (matches beta): strict left-to-right. activeSlotIndex
// starts at 0 and advances by one on each successful lock. The
// validator only checks `prev + word` for non-final slots; the final
// slot also validates `word + end`.

import { hasEdge, validBridgeWords, compoundDisplay, isKnownWord } from './compound-graph.js';
import { isDictWord } from './dictionary.js';
import { createLiveRegion, focusElement, prefersReducedMotion } from './a11y.js';
import { buildKeyboard } from './keyboard.js';
import { keyColorMap } from './key-colors.js';

const STATE_KEY_PREFIX = 'flow_fht_state:';
const TEST_STATE_KEY_PREFIX = 'flow_fht_test_state:';
const PROFANITY = new Set([
  // Conservative starter list — three-letter and short common words known
  // to flag in mainstream filters. Hits silently fail validation (player
  // sees the normal "not a valid compound" toast).
  // Real production: replace with LDNOOBW or similar.
  'shit', 'fuck', 'cunt', 'piss', 'cock', 'tits', 'twat',
]);

// ---- Hints per puzzle ----
// 2.14.29. ONE Letter hint a puzzle. Jeff, 2026-10-06: "reduce the hints from 4 to 1".
// It was three to six by tier (four on the daily). The tier no longer changes it.
function maxStarsForTier(tier) {
  return 1;
}

// ---------------------------------------------------------------------------
// Juice — tile cascade + confetti on Hard
// ---------------------------------------------------------------------------

function animateSolve(container, tier, onDone) {
  const tiles = Array.from(container.querySelectorAll('.flow-fht-tile'));
  let delay = 0;
  tiles.forEach((tile) => {
    setTimeout(() => {
      tile.classList.add('flow-fht-tile-pop');
      setTimeout(() => tile.classList.remove('flow-fht-tile-pop'), 1400);
    }, delay);
    delay += 480;
  });

  // Confetti fires on all tiers
  setTimeout(() => launchConfetti(container), delay - 200);

  // Post-game loads after all animations finish
  const totalDelay = delay + 800;
  setTimeout(onDone, totalDelay);
}

function launchConfetti(container) {
  const canvas = document.createElement('canvas');
  canvas.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:8000';
  document.body.appendChild(canvas);
  const ctx = canvas.getContext('2d');
  canvas.width = window.innerWidth;
  canvas.height = window.innerHeight;

  const colors = ['#c8553d','#5a8c3a','#2d6cdf','#e8a030','#8b5cf6','#ec4899'];
  const pieces = Array.from({ length: 80 }, () => ({
    x: Math.random() * canvas.width,
    y: -10 - Math.random() * 80,
    r: 4 + Math.random() * 5,
    color: colors[Math.floor(Math.random() * colors.length)],
    vx: (Math.random() - 0.5) * 4,
    vy: 3 + Math.random() * 4,
    rot: Math.random() * Math.PI * 2,
    vr: (Math.random() - 0.5) * 0.2,
    wide: Math.random() > 0.5,
  }));

  let frame;
  let start = null;
  const duration = 2200;

  function draw(ts) {
    if (!start) start = ts;
    const elapsed = ts - start;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const alpha = elapsed > duration - 400 ? Math.max(0, 1 - (elapsed - (duration - 400)) / 400) : 1;
    ctx.globalAlpha = alpha;
    pieces.forEach((p) => {
      p.x += p.vx;
      p.y += p.vy;
      p.vy += 0.12;
      p.rot += p.vr;
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      ctx.fillStyle = p.color;
      if (p.wide) {
        ctx.fillRect(-p.r, -p.r / 2, p.r * 2, p.r);
      } else {
        ctx.beginPath();
        ctx.arc(0, 0, p.r / 2, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    });
    if (elapsed < duration) {
      frame = requestAnimationFrame(draw);
    } else {
      canvas.remove();
    }
  }
  frame = requestAnimationFrame(draw);
  // Safety cleanup
  setTimeout(() => { cancelAnimationFrame(frame); canvas.remove(); }, duration + 200);
}

// ---- Persistence ----

function storageGet(key) {
  try { return window.localStorage.getItem(key); } catch (e) { return null; }
}
function storageSet(key, val) {
  try { window.localStorage.setItem(key, val); } catch (e) { /* ignore */ }
}
function storageRemove(key) {
  try { window.localStorage.removeItem(key); } catch (e) { /* ignore */ }
}

function stateKey(puzzleId, testMode) {
  return (testMode ? TEST_STATE_KEY_PREFIX : STATE_KEY_PREFIX) + puzzleId;
}

function loadState(puzzleId, testMode) {
  const raw = storageGet(stateKey(puzzleId, testMode));
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (e) {
    return null;
  }
}

function saveState(state) {
  // Don't persist the typed-but-not-submitted buffer or live timer fields.
  const toSave = { ...state };
  delete toSave.current_input;
  delete toSave._test_mode;
  delete toSave._hint_cache;
  delete toSave._last_activity_ms;
  delete toSave._design;
  storageSet(stateKey(state.puzzle_id, state._test_mode), JSON.stringify(toSave));
}

// ---- Active-time tracking ----
// Wall-clock time (finished - started) lied badly for anyone who left the
// game open: hours of idle read as hours of play. Instead, time accrues
// between interactions, and any single gap counts at most IDLE_CAP_MS.
// Think hard for ten minutes between letters: 90 seconds is charged.
// Leave the tab open all afternoon: 90 seconds is charged.
const IDLE_CAP_MS = 90 * 1000;

function touchActivity(state) {
  const now = Date.now();
  const last = (typeof state._last_activity_ms === 'number') ? state._last_activity_ms : now;
  const gap = Math.max(0, now - last);
  state.active_ms = (Number(state.active_ms) || 0) + Math.min(gap, IDLE_CAP_MS);
  state._last_activity_ms = now;
}

/**
 * Seconds of actual play. Falls back to the old wall-clock math for
 * states finished before active-time tracking existed.
 */
export function activeSeconds(state) {
  if (state && typeof state.active_ms === 'number' && state.active_ms > 0) {
    return Math.max(0, Math.round(state.active_ms / 1000));
  }
  if (state && state.finished_at_ms && state.started_at_ms) {
    return Math.max(0, Math.round((state.finished_at_ms - state.started_at_ms) / 1000));
  }
  return 0;
}

function freshState(puzzle) {
  const par = Number(puzzle.par || 1);
  const tier = String(puzzle.tier || 'easy').toLowerCase();
  const max = maxStarsForTier(tier);
  return {
    puzzle_id: Number(puzzle.puzzle_id),
    puzzle_version_id: Number(puzzle.puzzle_version_id || 0),
    start_word: String(puzzle.start_word || '').toLowerCase(),
    end_word: String(puzzle.end_word || '').toLowerCase(),
    tier,
    par,
    slots_filled: new Array(par).fill(''),
    slot_revealed: new Array(par).fill(false),
    slot_guesses: Array.from({ length: par }, () => []),
    // Which of the slot's valid answers the game has committed to. Null until
    // a guess actually finds a letter, or a hint is bought. Once set it never
    // changes, so pins, hints and the reveal can never disagree about the word.
    slot_answer: new Array(par).fill(null),
    letters_revealed: new Array(par).fill(0),
    length_revealed: new Array(par).fill(false),
    stars_remaining: max,
    max_stars: max,
    hints_used: 0,
    reveals_used: 0,
    invalid_attempts: 0,
    // Every Enter press that passed the real-word gate, right or wrong.
    guess_count: 0,
    // Active play time in ms. Grows only between interactions, and any
    // single gap counts at most IDLE_CAP_MS — so a tab left open for
    // hours reads as minutes of actual play, not wall-clock hours.
    active_ms: 0,
    status: 'in-progress', // 'in-progress' | 'solved' | 'failed'
    active_slot_index: 0,
    current_input: '',
    // ms timestamp the puzzle was first opened. Kept for reference;
    // time_seconds now comes from active_ms (see touchActivity), which
    // ignores time the game sat open untouched.
    started_at_ms: Date.now(),
    finished_at_ms: null,
    submitted_to_server: false,
  };
}

// ---- Public API ----

/**
 * Mount the game into `container`. The puzzle object is the unwrapped
 * `/today` response (so puzzle.start_word, puzzle.end_word, puzzle.tier,
 * puzzle.par, puzzle.puzzle_id, puzzle.puzzle_version_id).
 *
 * `onComplete(state)` is called when the puzzle ends (solved or failed).
 * The shell submits to /plays and renders the post-game UI.
 */
export function mountGame(container, puzzle, callbacks) {
  callbacks = callbacks || {};
  const testMode = !!callbacks.testMode;
  const normalised = {
    puzzle_id: puzzle.puzzle_id,
    puzzle_version_id: puzzle.puzzle_version_id,
    start_word: (puzzle.content && puzzle.content.start_word) || puzzle.start_word,
    end_word: (puzzle.content && puzzle.content.end_word) || puzzle.end_word,
    tier: (puzzle.content && puzzle.content.tier) || puzzle.tier,
    par: (puzzle.content && puzzle.content.par) || puzzle.par,
    design: (puzzle.content && puzzle.content.design) || puzzle.design || null,
  };

  // Both modes resume from saved state — but each mode has its own
  // localStorage prefix so practice plays and real plays of the same
  // puzzle don't trample each other.
  const existing = loadState(normalised.puzzle_id, testMode);
  let state;
  if (existing && existing.puzzle_version_id === normalised.puzzle_version_id) {
    // Resume — but always reset transient input/active slot from saved snapshot.
    state = { ...existing, current_input: '' };
    if (typeof state.active_slot_index !== 'number') {
      state.active_slot_index = firstEmptySlot(state);
    }
    // 2.14.29: one hint a puzzle. A game begun on an earlier build was begun with three to
    // six, and merely opening the day's puzzle saves it, so without this the day the build is
    // installed would still read "0/4". If it has used none it has the one; if it has used
    // any, its hint is spent and the count stays what it used ("2/2 used"), so the end screen
    // still tells the truth about that game.
    if (state.status === 'in-progress' && Number(state.max_stars) > 1) {
      const usedBefore = Math.max(0, (Number(state.max_stars) || 0) - (Number(state.stars_remaining) || 0));
      state.max_stars = Math.max(1, usedBefore);
      state.stars_remaining = state.max_stars - usedBefore;
    }
  } else {
    // No saved state or stale version — start clean.
    state = freshState(normalised);
  }
  state._test_mode = testMode;
  // The puzzle as it was built (2.14.23): each designed bridge word's length
  // and fingerprint, from the server. Never saved — it comes with the puzzle.
  state._design = validDesign(normalised.design, state.par);
  state._hint_cache = {};
  // Older saved states won't have slot_guesses — backfill so coloring works.
  if (!Array.isArray(state.slot_guesses) || state.slot_guesses.length !== state.par) {
    const g = Array.from({ length: state.par }, () => []);
    if (Array.isArray(state.slot_guesses)) {
      for (let i = 0; i < state.par && i < state.slot_guesses.length; i++) {
        if (Array.isArray(state.slot_guesses[i])) g[i] = state.slot_guesses[i];
      }
    }
    state.slot_guesses = g;
  }
  // Older saves predate the committed answer. Start them uncommitted; the
  // guesses they already carry re-commit on the next render.
  if (!Array.isArray(state.slot_answer) || state.slot_answer.length !== state.par) {
    const a = new Array(state.par).fill(null);
    if (Array.isArray(state.slot_answer)) {
      for (let i = 0; i < state.par && i < state.slot_answer.length; i++) a[i] = state.slot_answer[i] || null;
    }
    state.slot_answer = a;
  }
  // Older saves predate guess counting / active-time. Start them at zero
  // (their past idle time is forgiven).
  if (typeof state.guess_count !== 'number') state.guess_count = 0;
  if (typeof state.active_ms !== 'number') state.active_ms = 0;
  state._last_activity_ms = Date.now();
  saveState(state);

  // Build the static structure once. Re-renders mutate children in place.
  container.innerHTML = '';
  container.classList.add('flow-fht-game');

  const liveRegion = createLiveRegion(container);

  const chain = document.createElement('div');
  chain.className = 'flow-fht-chain flow-fht-chain-game';
  chain.setAttribute('role', 'group');
  chain.setAttribute('aria-label',
    'Puzzle: from ' + state.start_word.toUpperCase() +
    ' to ' + state.end_word.toUpperCase());
  container.appendChild(chain);

  const hintsBar = document.createElement('div');
  hintsBar.className = 'flow-fht-hints-bar';
  container.appendChild(hintsBar);
  // 2.14.29: "Show answers" has been tapped once and is asking (never saved)
  let endArmed = false;
  let endArmTimer = null;
  // when the Show answers box appeared, and when it was first pressed (see showAnswers)
  const END_QUIET_MS = 1000;
  const END_GAP_MS = 500;
  const tick = () => ((typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now());
  let endShownAt = tick();
  let endArmedAt = 0;
  let endQuietTimer = null;

  // Keyboard module returns a wrapper element plus a handler we call
  // from physical-keyboard events for parity.
  const keyboard = buildKeyboard({
    onLetter: (letter) => onPhysicalKey(letter.toUpperCase()),
    onBackspace: () => onPhysicalKey('BACKSPACE'),
    onSubmit: () => onPhysicalKey('ENTER'),
  });
  container.appendChild(keyboard.element);

  function onPhysicalKey(key) {
    if (state.status !== 'in-progress') return;
    touchActivity(state);
    // 2.14.29: going on playing takes back the first tap on "Show answers"
    if (disarmEnd()) renderHintsBar();

    if (key === 'BACKSPACE') {
      if (state.current_input.length > 0) {
        state.current_input = state.current_input.slice(0, -1);
        renderActiveSlot();
      }
      return;
    }
    if (key === 'ENTER') {
      submit();
      return;
    }
    if (/^[A-Z]$/.test(key)) {
      const slotIdx = state.active_slot_index;
      // Typed letters fill only the blank (unknown) positions. Letters already
      // known for free — hint letters and confirmed-green letters — are
      // auto-filled and skipped, so the cap is just the number of blanks.
      const { blanks, len } = knownLettersForSlot(slotIdx);
      const cap = (len == null) ? 14 : blanks.length;
      if (state.current_input.length < cap) {
        state.current_input += key.toLowerCase();
        renderActiveSlot();
      }
    }
  }

  // Physical-keyboard handler — only acts when the game container is in
  // focus or no other input/textarea is. We attach to the container so
  // closing the game also detaches.
  function onKeyDown(e) {
    touchActivity(state);
    if (state.status !== 'in-progress') return;
    // Don't hijack typing in actual form inputs.
    const t = e.target;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) {
      return;
    }
    // 2.14.29: a key of play typed while a hint box has the focus is play: the focus goes
    // back to the board first. (The bar is redrawn after every move, which drops the box and
    // the focus with it; a keyboard player who had pressed Show answers once and then typed
    // on was left with keys that nothing heard. Found by the independent check of this
    // build.) Only for the real keyboard: a tap on an on-screen key moves no focus, as on
    // every build before this one.
    if ((e.key === 'Backspace' || e.key === 'Enter' || (e.key.length === 1 && /[A-Za-z]/.test(e.key))) &&
        hintsBar.contains(document.activeElement)) focusBoard();
    if (e.key === 'Backspace') {
      e.preventDefault();
      onPhysicalKey('BACKSPACE');
    } else if (e.key === 'Enter') {
      e.preventDefault();
      onPhysicalKey('ENTER');
    } else if (e.key.length === 1 && /[A-Za-z]/.test(e.key)) {
      e.preventDefault();
      onPhysicalKey(e.key.toUpperCase());
    }
  }
  container.addEventListener('keydown', onKeyDown);
  // Make the container focusable so it can receive keystrokes.
  container.setAttribute('tabindex', '0');
  // 2.14.29: how the focus got where it is, by the Tab key or by a click or tap. When the
  // hint box is redrawn ("Show answers" to "Tap again") a keyboard player's focus stays on it.
  let tabbing = false;
  const onTabKey = (e) => { if (e.key === 'Tab') tabbing = true; };
  const onPointer = () => { tabbing = false; };
  document.addEventListener('keydown', onTabKey, true);
  document.addEventListener('pointerdown', onPointer, true);
  // 2.14.29: the board takes the focus WITHOUT scrolling the page. focusElement() scrolls
  // its target into view; on a phone that slid the on-screen keys out from under the
  // thumb, and the next tap landed on another key or on the hint box (found by the third
  // independent pass of this build).
  const focusBoard = () => { try { container.focus({ preventScroll: true }); } catch (e) { container.focus(); } };

  // ---- Toast (transient status messages) ----

  let toastTimer = null;
  function toast(text, ms) {
    let el = container.querySelector('.flow-fht-toast');
    if (!el) {
      el = document.createElement('div');
      el.className = 'flow-fht-toast';
      el.setAttribute('role', 'status');
      container.appendChild(el);
    }
    el.textContent = text;
    el.classList.add('visible');
    if (toastTimer) clearTimeout(toastTimer);
    toastTimer = setTimeout(() => {
      el.classList.remove('visible');
    }, ms || 1800);
  }

  // ---- Rendering ----

  function render() {
    renderChain();
    renderHintsBar();
    keyboard.refresh(currentMaxInputCap());
    keyboard.setDisabled(state.status !== 'in-progress');
    applyKeyboardColors();
  }

  // How many past-guess rows the active slot shows. Bounded so a long
  // stretch of guessing can't grow the tile past the screen.
  const COLOR_WORDS = { green: 'right spot', yellow: 'wrong spot', absent: 'not in the word' };

  // The guess whose colours should be spoken on the next shake. Set by
  // recordGuess and consumed once, so the readout only ever follows a guess
  // that actually earned colours — profanity and non-words are turned away
  // uncoloured and unrecorded. This is where the removed history rows' screen
  // reader labels went; sighted players read the same thing off the live row
  // and the keyboard.
  let pendingSpokenGuess = null;

  // Compute Wordle-style coloring for the active (or any) slot. Past
  // guesses are colored against the single valid bridge word the player's
  // guesses collectively point at — so on a slot that accepts several
  // words, the colors follow whichever one you're closest to. Returns
  // colored rows plus accumulated keyboard letter-state sets.
  // resolveSlot — the ONE place that decides two things for a slot: which
  // answer we are colouring against, and which letters are pinned onto the
  // board. Both used to be worked out separately and by different rules,
  // which is how the board ended up refusing to pin letters the guess rows
  // were already showing as green.
  //
  // Jeff's rule (2026-08-19): when a slot accepts more than one answer, the
  // answer the player's guesses first point at is the one that sticks — one
  // word, not two. We walk the guesses in order; after each one the live
  // candidate set narrows to the answers that agree with everything pinned
  // so far. A pinned letter can therefore never be contradicted by a later
  // target, so a green never moves once it lands.
  //
  // Hint-revealed letters are seeded as pins before any guess is read, so a
  // hint and a pin can never disagree about the same position.
  function resolveSlot(slotIdx) {
    const guesses = (state.slot_guesses && state.slot_guesses[slotIdx]) || [];
    const prev = slotIdx === 0 ? state.start_word : state.slots_filled[slotIdx - 1];
    const slotsAfter = state.par - 1 - slotIdx;
    const all = validBridgeWords(prev, state.end_word, slotsAfter);
    const canonical = canonicalForSlot(state, slotIdx);
    const len = canonical ? canonical.length : null;
    const committed = (state.slot_answer && state.slot_answer[slotIdx]) || null;

    // Once a slot has committed to an answer, that answer is the only one
    // left alive — which is what stops a hint and a pin ever disagreeing.
    let live;
    if (committed) {
      live = [committed];
    } else {
      live = (len == null) ? all.slice() : all.filter((w) => w.length === len);
      if (!live.length) live = all.slice();
    }

    const pins = {};
    const narrow = () => {
      const keys = Object.keys(pins);
      if (!keys.length) return;
      const next = live.filter((w) => keys.every((i) => w[i] === pins[i]));
      if (next.length) live = next;
    };

    // Letters the player bought with a hint come off the committed answer.
    const answerSoFar = committed || canonical;
    const revealed = state.letters_revealed[slotIdx] || 0;
    if (answerSoFar) {
      for (let i = 0; i < revealed && i < answerSoFar.length; i++) pins[i] = answerSoFar[i];
      narrow();
    }

    const seen = [];
    for (const g of guesses) {
      seen.push(g);
      const t = chooseColorTarget(seen, live, canonical);
      if (!t) break;
      for (let i = 0; i < g.length && i < t.length; i++) {
        if (g[i] === t[i]) pins[i] = g[i];
      }
      narrow();
    }

    const target = chooseColorTarget(guesses, live, canonical);
    return { target, pins, live, all, len, committed };
  }

  // The word this slot has committed to — what hints reveal, what the board
  // pins, and what a guess is coloured against. Falls back to the canonical
  // completion for a slot nothing has touched yet.
  function answerForSlot(slotIdx) {
    return (state.slot_answer && state.slot_answer[slotIdx])
      || resolveSlot(slotIdx).target
      || canonicalForSlot(state, slotIdx);
  }

  // Lock this slot onto one answer. A guess commits only when it actually
  // FINDS something (Jeff's rule: the word the player's first real hit points
  // at is the one that sticks) — a guess that lands nothing leaves the slot
  // open. Buying a hint commits outright, since a hint hands over a letter of
  // a specific word.
  function commitAnswer(slotIdx, force) {
    if (!Array.isArray(state.slot_answer)) state.slot_answer = new Array(state.par).fill(null);
    if (state.slot_answer[slotIdx]) return state.slot_answer[slotIdx];
    const r = resolveSlot(slotIdx);
    if (!r.target) return null;
    if (!force && !Object.keys(r.pins).length) return null;
    state.slot_answer[slotIdx] = r.target;
    return r.target;
  }

  function deriveSlotColors(slotIdx) {
    const guesses = (state.slot_guesses && state.slot_guesses[slotIdx]) || [];
    const resolved = resolveSlot(slotIdx);
    const V = resolved.live;
    const target = resolved.target;
    // No valid answer to colour against (a momentarily broken / dead-end slot).
    // Pin nothing and grey nothing — never colour a guess against ITSELF, which
    // used to paint every letter green and jam the board with no blanks to type.
    if (!target) {
      return { rows: [], green: new Set(), yellow: new Set(), absent: new Set(), greenPos: {}, target: null };
    }
    const rows = guesses.map((g) => ({ guess: g, colors: colorWord(g, target) }));

    const green = new Set();
    const yellow = new Set();
    const absent = new Set();
    const union = new Set();
    // greenPos: position index -> lowercase letter for every spot the player
    // has confirmed green (right letter, right place). Used to pin those
    // letters onto the playboard as a persistent guide.
    const greenPos = {};
    V.forEach((w) => { for (const ch of w) union.add(ch); });
    rows.forEach((r) => {
      for (let i = 0; i < r.guess.length; i++) {
        const c = r.colors[i];
        const L = r.guess[i];
        if (c === 'green') { green.add(L); greenPos[i] = L; }
        else if (c === 'yellow') yellow.add(L);
      }
    });
    // Only grey a key if the letter can't appear in ANY valid answer for
    // this slot — so the colors never lie on multi-answer puzzles.
    rows.forEach((r) => {
      for (const L of r.guess) {
        if (!green.has(L) && !yellow.has(L) && !union.has(L)) absent.add(L);
      }
    });
    return { rows, green, yellow, absent, greenPos, target };
  }

  // knownLettersForSlot — which positions in the active word are already
  // known for free, so the player doesn't have to type them. That's the
  // leading run of hint-revealed letters plus every position the player has
  // confirmed green. Returns { known: {pos: letter}, blanks: [pos...], len }.
  // Typed letters fill the blanks left-to-right and skip the known positions.
  function knownLettersForSlot(slotIdx) {
    const known = {};
    const len = currentHintLengthForSlot(state, slotIdx);
    const canonical = ((state.slot_answer && state.slot_answer[slotIdx])
      || canonicalForSlot(state, slotIdx) || '');
    const revealed = state.letters_revealed[slotIdx] || 0;
    // Letters revealed by hints (always a leading run) are known for free.
    for (let i = 0; i < revealed && i < canonical.length; i++) {
      known[i] = canonical[i];
    }

    if (len != null) {
      // A position auto-fills green when the player has guessed the right
      // letter there in the answer the game has committed to for this slot.
      // resolveSlot owns that decision and guarantees a pinned letter can
      // never be contradicted later, so a green never moves once it lands.
      const { pins } = resolveSlot(slotIdx);
      Object.keys(pins).forEach((i) => {
        if (Number(i) < len) known[i] = pins[i];
      });
    }

    const blanks = [];
    if (len != null) {
      for (let i = 0; i < len; i++) { if (known[i] == null) blanks.push(i); }
    }
    return { known, blanks, len };
  }

  // assembleWord — rebuild the full guess string for a slot from its known
  // letters (fixed spots) and the player's typed letters (filling the blanks
  // in order). Used for both submit and the spoken slot label.
  function assembleWord(slotIdx, typed) {
    const { known, len } = knownLettersForSlot(slotIdx);
    typed = typed || '';
    if (len == null) {
      let lead = '';
      let i = 0;
      while (known[i] != null) { lead += known[i]; i++; }
      return (lead + typed).toLowerCase();
    }
    let out = '';
    let t = 0;
    for (let i = 0; i < len; i++) {
      if (known[i] != null) {
        out += known[i];
      } else {
        if (typed[t] != null) out += typed[t];
        t += 1;
      }
    }
    return out.toLowerCase();
  }

  function applyKeyboardColors() {
    if (state.status !== 'in-progress') { keyboard.setKeyColors({}); return; }
    const info = deriveSlotColors(state.active_slot_index);
    const map = {};
    // Greens are pinned onto the playboard in their correct spots, so the
    // keyboard carries only the "somewhere / not in the word" memory:
    // yellow keys and grey keys.
    //
    // Jeff, 2026-09-23: stale yellows. The rule, and why, is in key-colors.js,
    // which the harness runs directly against a truth table.
    Object.assign(map, keyColorMap({
      yellow: info.yellow,
      absent: info.absent,
      known: knownLettersForSlot(state.active_slot_index).known,
      target: info.target,
    }));

    keyboard.setKeyColors(map);
  }

  function renderChain() {
    chain.innerHTML = '';

    // Top word: start_word
    chain.appendChild(makeFixedTile(state.start_word, 'start'));
    chain.appendChild(makeArrow());

    // Bridge slots
    for (let i = 0; i < state.par; i++) {
      const isLocked = state.slots_filled[i] !== '';
      if (isLocked) {
        chain.appendChild(makeLockedTile(state.slots_filled[i], state.slot_revealed[i]));
      } else if (i === state.active_slot_index && state.status === 'in-progress') {
        chain.appendChild(makeActiveTile(i));
      } else {
        chain.appendChild(makeEmptyTile(i));
      }
      chain.appendChild(makeArrow());
    }

    chain.appendChild(makeFixedTile(state.end_word, 'end'));
  }

  function renderActiveSlot() {
    // Only the active slot tile changes when the typed buffer mutates.
    renderChain();
  }

  function makeFixedTile(word, role) {
    const tile = document.createElement('div');
    tile.className = 'flow-fht-tile flow-fht-tile-' + role + ' flow-fht-tile-fixed';
    tile.textContent = String(word || '').toUpperCase();
    return tile;
  }

  function makeLockedTile(word, wasRevealed) {
    const tile = document.createElement('div');
    tile.className = 'flow-fht-tile flow-fht-tile-locked' +
      (wasRevealed ? ' flow-fht-tile-revealed' : '');
    tile.textContent = String(word || '').toUpperCase();
    return tile;
  }

  function makeEmptyTile(slotIdx) {
    const tile = document.createElement('div');
    tile.className = 'flow-fht-tile flow-fht-tile-empty';
    tile.setAttribute('aria-label', 'Bridge slot ' + (slotIdx + 1) + ', not yet active');
    // One empty box per letter, the SAME box the slot you are typing in shows
    // (2.14.21; they were small dots). Jeff, 2026-09-30: "when we have two
    // words to match in thruWord, the second line is now dots. I'd prefer it to
    // be a rectangle or square placeholder like in the first row." The slot
    // keeps its dashed frame, so it still reads as the one that comes next.
    const len = currentHintLengthForSlot(state, slotIdx);
    const boxWrap = document.createElement('span');
    boxWrap.className = 'flow-fht-active-letters';
    const boxCount = len || 3;
    for (let d = 0; d < boxCount; d++) {
      const box = document.createElement('span');
      box.className = 'flow-fht-cell flow-fht-cell-empty';
      boxWrap.appendChild(box);
    }
    tile.appendChild(boxWrap);
    return tile;
  }

  function makeActiveTile(slotIdx) {
    const tile = document.createElement('div');
    tile.className = 'flow-fht-tile flow-fht-tile-active';
    tile.id = 'flow-fht-active-slot';
    const knownLength = currentHintLengthForSlot(state, slotIdx);

    // Letters already known for free at this slot: hint-revealed letters plus
    // any position the player has confirmed green. These auto-fill in solid
    // green and are skipped while typing — the player never retypes a green.
    const { known } = knownLettersForSlot(slotIdx);

    const rowsWrap = document.createElement('div');
    rowsWrap.className = 'flow-fht-guess-rows';

    // No guess history. One live row (2026-08-19, Jeff): greens hold their
    // place in the row you are typing, and the keyboard carries yellow and
    // grey. The stacked history that used to sit here existed only because
    // the board refused to pin a green on a slot with more than one answer;
    // resolveSlot commits to a single answer now, so the green always has
    // somewhere to land and the history has nothing left to say.

    // Single current-input row. Word length is always visible.
    const inputRow = document.createElement('div');
    inputRow.className = 'flow-fht-guess-row flow-fht-guess-input';
    const wrap = document.createElement('span');
    wrap.className = 'flow-fht-active-letters';

    if (knownLength) {
      const typed = state.current_input;
      let t = 0; // index into the typed buffer; advances on blanks only
      for (let pos = 0; pos < knownLength; pos++) {
        const cell = document.createElement('span');
        let letter = '';
        let cls = 'flow-fht-cell';
        if (known[pos] != null) {
          // Known for free (hint letter or confirmed green): solid green,
          // auto-filled, never typed into.
          letter = known[pos];
          cls += ' flow-fht-cell-green';
        } else {
          // A blank the player fills by typing. Typed letters flow into the
          // blanks in order and skip the green positions entirely.
          if (typed[t] != null) {
            letter = typed[t];
            cls += ' flow-fht-cell-typed';
          } else {
            cls += ' flow-fht-cell-empty';
          }
          t += 1;
        }
        cell.className = cls;
        if (letter) cell.textContent = letter.toUpperCase();
        wrap.appendChild(cell);
      }
    } else {
      // Length not yet known (rare) — keep a simple placeholder.
      const sp = document.createElement('span');
      sp.className = 'flow-fht-letter-tail';
      sp.textContent = '\u00b7\u00b7\u00b7';
      wrap.appendChild(sp);
    }
    inputRow.appendChild(wrap);
    rowsWrap.appendChild(inputRow);
    tile.appendChild(rowsWrap);

    const typedWord = assembleWord(slotIdx, state.current_input).toUpperCase();
    tile.setAttribute('aria-label',
      'Active slot, current text: ' + (typedWord || 'empty') +
      (knownLength ? '. Bridge is ' + knownLength + ' letters.' : ''));
    return tile;
  }

  function makeArrow() {
    const arrow = document.createElement('span');
    arrow.className = 'flow-fht-arrow';
    arrow.setAttribute('aria-hidden', 'true');
    arrow.textContent = '\u2193';
    return arrow;
  }

  function renderHintsBar(keepOnBox) {
    // 2.14.29: redrawing the bar drops the box that had the focus. Put the focus back:
    // on the new box when the caller asks (a keyboard player between the two presses of
    // Show answers), otherwise on the board, so the keys are still heard.
    const hadFocus = hintsBar.contains(document.activeElement);
    hintsBar.innerHTML = '';
    if (state.status !== 'in-progress') return;
    if (hadFocus) setTimeout(() => {
      const box = hintsBar.querySelector('.flow-fht-hint-box');
      if (keepOnBox && box) { try { box.focus({ preventScroll: true }); } catch (e) { box.focus(); } }
      else if (state.status === 'in-progress') focusBoard();
    }, 0);

    const noStars = state.stars_remaining <= 0;

    // "Hint" label
    const hintLabel = document.createElement('span');
    hintLabel.className = 'flow-fht-hint-label';
    hintLabel.textContent = 'Hint';
    hintsBar.appendChild(hintLabel);

    /* BOXES, NOT CIRCLED LETTERS (2.14.12). Jeff, 2026-09-28: "for thruWord
       the second clue when not being used are tiny graphics. let's convert
       them to boxes." A circled L and W said nothing on their own, and the W —
       held back on Level 1 until the hints run out — faded to almost nothing.
       Each is now a labelled box, and a held-back one says when it opens. */
    /* ONE BOX, AND WHAT IT IS CHANGES ONCE (2.14.29). Jeff, 2026-10-06: "reduce the
       hints from 4 to 1"; asked whether the hint should give its letter and let
       the puzzle go on, the box then reading "Show answers": "Yes (Recommended)".
       While the hint is unspent the box is Letter. Once it is spent the box is
       Show answers, which ends the puzzle and shows its words, and asks once
       first ("Tap again to end today's puzzle"): two taps, never a browser
       dialog, as Reset does in Settings. The two taps cannot be one burst: the
       box takes no press in its first second, and its second press counts only
       half a second after the first (see showAnswers). Until this build the LAST hint ended
       the puzzle by itself, with nothing on screen to say so; with one hint
       that would have made the only hint a give-up button.
       (The whole-word hint left in 2.14.28: "we are also taking out the hint
       for a full word.") */
    if (!noStars) {
      const letterBtn = document.createElement('button');
      letterBtn.type = 'button';
      letterBtn.className = 'flow-fht-hint-box';
      letterBtn.innerHTML = '<span class="flow-fht-hint-box-t">Letter</span>';
      letterBtn.setAttribute('aria-label', 'Letter hint — reveal the next letter (your one hint)');
      letterBtn.addEventListener('click', () => useHint('letter'));
      hintsBar.appendChild(letterBtn);
    } else {
      const endBtn = document.createElement('button');
      endBtn.type = 'button';
      endBtn.className = 'flow-fht-hint-box flow-fht-hint-end' + (endArmed ? ' is-armed' : '');
      endBtn.innerHTML = endArmed
        ? '<span class="flow-fht-hint-box-t">Tap again</span>' +
          '<span class="flow-fht-hint-box-s">' + endWords() + '</span>'
        : '<span class="flow-fht-hint-box-t">Show answers</span>';
      endBtn.setAttribute('aria-label', endArmed
        ? 'Tap again ' + endWords()
        : 'Show answers — ends the puzzle; it asks once first');
      endBtn.addEventListener('click', showAnswers);
      // Its first second (see showAnswers): drawn as not yet live, so a press that does
      // nothing is seen to do nothing, and made live when the second is up.
      const quiet = END_QUIET_MS - (tick() - endShownAt);
      if (quiet > 0 && !endArmed) {
        // NOT the disabled attribute: a disabled button is no stop for the Tab key, so a
        // keyboard player's Tab went past it to the on-screen Q (the third independent
        // pass). It is made live where it stands, not redrawn, so the focus stays on it.
        endBtn.classList.add('is-waiting');
        endBtn.setAttribute('aria-disabled', 'true');
        if (endQuietTimer) clearTimeout(endQuietTimer);
        endQuietTimer = setTimeout(() => {
          endQuietTimer = null;
          const b = hintsBar.querySelector('.flow-fht-hint-end.is-waiting');
          if (b) { b.classList.remove('is-waiting'); b.removeAttribute('aria-disabled'); }
        }, quiet + 20);
      }
      hintsBar.appendChild(endBtn);
    }

    // Hints-used counter — after the hint buttons. Shows "used / total"
    // to match the finish line ("Solved · 1/3 hints") and the share card.
    const used = Math.max(0, (state.max_stars || 0) - (state.stars_remaining || 0));
    const hintsCountEl = document.createElement('div');
    hintsCountEl.className = 'flow-fht-hints-count';
    hintsCountEl.setAttribute('aria-label',
      used + ' of ' + state.max_stars + ' hints used');
    const countValue = document.createElement('span');
    countValue.className = 'flow-fht-hints-count-value';
    countValue.textContent = used + '/' + state.max_stars;
    countValue.setAttribute('aria-hidden', 'true');
    hintsCountEl.appendChild(countValue);
    const countLabel = document.createElement('span');
    countLabel.className = 'flow-fht-hints-count-label';
    countLabel.textContent = 'used';
    countLabel.setAttribute('aria-hidden', 'true');
    hintsCountEl.appendChild(countLabel);
    hintsBar.appendChild(hintsCountEl);
  }

  function currentMaxInputCap() {
    if (state.status !== 'in-progress') return 0;
    const slotIdx = state.active_slot_index;
    const { blanks, len } = knownLettersForSlot(slotIdx);
    return (len == null) ? 14 : blanks.length;
  }

  // ---- Submit ----

  // Record a wrong (but non-profanity) guess so it shows as a colored row
  // and feeds the keyboard. Skips consecutive duplicates to avoid clutter.
  function recordGuess(slotIdx, word) {
    if (!state.slot_guesses) state.slot_guesses = [];
    if (!Array.isArray(state.slot_guesses[slotIdx])) state.slot_guesses[slotIdx] = [];
    const arr = state.slot_guesses[slotIdx];
    if (arr[arr.length - 1] !== word) arr.push(word);
    pendingSpokenGuess = word;
    // If this guess landed a letter, the slot locks onto the answer it points
    // at and stays there.
    commitAnswer(slotIdx);
  }

  function submit() {
    if (state.status !== 'in-progress') return;
    touchActivity(state);
    const slotIdx = state.active_slot_index;
    // Assemble the full word: known letters (hints + confirmed greens) in
    // their fixed spots, with the player's typed letters filling the blanks
    // around them.
    const word = assembleWord(slotIdx, state.current_input).toLowerCase();
    if (!word) {
      toast('Type a bridge word, then press Enter.');
      return;
    }

    // Profanity: silent reject. Player retypes; no acknowledgement.
    if (PROFANITY.has(word)) {
      state.invalid_attempts += 1;
      shakeAndClear('Not a valid compound. Try another word.');
      saveState(state);
      return;
    }

    // Real-word gate — Wordle's two-list shape. Any real English word
    // earns letter feedback (the ~152K-word accept-list, loaded in the
    // background); only non-words are turned away, uncolored and
    // unrecorded. Without this, alphabet-soup entries (ABCDE, FGHIJ...)
    // light up the whole keyboard for free — a beta tester found
    // exactly that. If the big list hasn't arrived yet, the game's own
    // compound vocabulary stands in (strict for a moment, never broken).
    const inDict = isDictWord(word);
    const isRealWord = (inDict === null) ? isKnownWord(word) : inDict;
    if (!isRealWord) {
      state.invalid_attempts += 1;
      shakeAndClear('Not a word we recognize. Try a real word.');
      saveState(state);
      return;
    }

    // A real-word Enter press is a guess, right or wrong.
    state.guess_count = (Number(state.guess_count) || 0) + 1;

    const isFirst = slotIdx === 0;
    const isLast = slotIdx === state.par - 1;
    const prev = isFirst ? state.start_word : state.slots_filled[slotIdx - 1];

    // Validate prev + word forms a real compound.
    if (!hasEdge(prev, word)) {
      state.invalid_attempts += 1;
      recordGuess(slotIdx, word);
      shakeAndClear((prev + word).toUpperCase() + ' isn\u2019t a valid compound.');
      saveState(state);
      return;
    }
    // If last slot, also validate word + end_word.
    if (isLast) {
      if (!hasEdge(word, state.end_word)) {
        state.invalid_attempts += 1;
        recordGuess(slotIdx, word);
        shakeAndClear((word + state.end_word).toUpperCase() + ' isn\u2019t a valid compound.');
        saveState(state);
        return;
      }
    } else {
      // Non-last slots: enforce that the chosen word leaves a valid
      // continuation path to end_word. Without this check the player
      // could lock something like WIND -> NOG just because WIND+NOG is
      // a real compound, and then dead-end at the next slot because no
      // valid NOG+? -> SHOT chain exists. Reject those up front so the
      // player can't paint themselves into a corner.
      const slotsAfter = state.par - 1 - slotIdx;
      // From word we need a chain that uses slotsAfter more bridges
      // and then forms a compound with end_word. validBridgeWords
      // checks "from prev, what words lead to end_word in slotsAfter
      // hops?" — for the lock itself, that means slotsAfter - 1 more
      // bridge slots remain after `word`.
      if (validBridgeWords(word, state.end_word, slotsAfter - 1).length === 0) {
        state.invalid_attempts += 1;
        recordGuess(slotIdx, word);
        shakeAndClear(compoundDisplay(prev, word).toUpperCase() +
          ' is a real compound, but it leads to a dead end. Try another word.');
        saveState(state);
        return;
      }
    }

    // Lock the slot.
    state.slots_filled[slotIdx] = word;
    state.slot_revealed[slotIdx] = false;
    state.current_input = '';
    state.active_slot_index = slotIdx + 1;
    // Clear projected canonical cache — future slots now have a real prev
    state._hint_cache = {};

    const compound = compoundDisplay(prev, word).toUpperCase();
    flash('\u2713 ' + compound);
    liveRegion.announce('Locked: ' + compound + '.');

    afterStateChange();
  }

  function shakeAndClear(message) {
    const tile = container.querySelector('#flow-fht-active-slot');
    if (tile && !prefersReducedMotion()) {
      tile.classList.add('flow-fht-shake');
      setTimeout(() => { tile.classList.remove('flow-fht-shake'); }, 450);
    }
    state.current_input = '';
    toast(message, 2200);
    liveRegion.announce(message + spokenColorsFor(pendingSpokenGuess));
    pendingSpokenGuess = null;
    render();
  }

  // Read a guess back letter by letter — "F right spot, O right spot, R not in
  // the word, T right spot" — against the answer this slot is committed to.
  function spokenColorsFor(guess) {
    if (!guess) return '';
    const target = resolveSlot(state.active_slot_index).target;
    if (!target) return '';
    const colors = colorWord(guess, target);
    const parts = [];
    for (let i = 0; i < guess.length; i++) {
      parts.push(guess[i].toUpperCase() + ' ' + COLOR_WORDS[colors[i]]);
    }
    return ' ' + guess.toUpperCase() + ': ' + parts.join(', ') + '.';
  }

  function flash(text) {
    toast(text, 1200);
  }

  // ---- Hints ----

  function useHint(kind) {
    touchActivity(state);
    if (state.status !== 'in-progress') return;
    const slotIdx = state.active_slot_index;
    if (kind === 'letter') {
      if (state.stars_remaining <= 0) return;
      // A hint hands over a letter of one specific word, so it commits the
      // slot. Without this the hint used to read off the canonical answer
      // while the board was pinned to a different one, and the revealed
      // letters overwrote the player's own greens.
      commitAnswer(slotIdx, true);
      const canonical = answerForSlot(slotIdx);
      if (!canonical) return;
      if (state.letters_revealed[slotIdx] >= canonical.length) return;

      // Skip any leading letters the player has already confirmed green (and
      // that match the answer) so the hint reveals a genuinely new letter
      // rather than one they already found. Example: if they've guessed the
      // first letter, the hint brings in the second.
      const knownNow = knownLettersForSlot(slotIdx).known || {};
      let idx = state.letters_revealed[slotIdx];
      while (idx < canonical.length && knownNow[idx] === canonical[idx]) {
        idx += 1;
      }

      if (idx >= canonical.length) {
        // Every letter is already confirmed by the player's own guesses, so
        // there's nothing new to reveal — just complete the word, free.
        state.slots_filled[slotIdx] = canonical;
        state.slot_revealed[slotIdx] = true;
        state.letters_revealed[slotIdx] = canonical.length;
        state.current_input = '';
        state.active_slot_index = slotIdx + 1;
        liveRegion.announce('All letters already found. ' + canonical.toUpperCase() + ' completed.');
        afterStateChange();
        return;
      }

      // Reveal the next unknown letter (it shows on the board in green) and
      // spend one hint. Reset any typed-in letters so the grown prefix and the
      // typed remainder stay lined up.
      state.letters_revealed[slotIdx] = idx + 1;
      state.stars_remaining -= 1;
      state.hints_used += 1;
      if (state.stars_remaining <= 0) endShownAt = tick();   // 2.14.29: the box becomes Show answers now
      state.current_input = '';
      liveRegion.announce('Letter hint: ' + canonical[idx].toUpperCase() + '.');
      // Auto-solve if the whole word is now known.
      if (state.letters_revealed[slotIdx] >= canonical.length) {
        state.slots_filled[slotIdx] = canonical;
        state.slot_revealed[slotIdx] = true;
        state.active_slot_index = slotIdx + 1;
      }
    } else {
      // 2.14.28: 'letter' is the only hint. The whole-word hint ('reveal') is gone,
      // and nothing else may spend a hint or fill a word by this road.
      return;
    }
    afterStateChange();
  }

  // ---- End-of-puzzle ----

  function afterStateChange() {
    updateStatus();
    saveState(state);
    render();
    if (state.status !== 'in-progress') {
      if (state.status === 'solved') {
        // Cascade animation first, then hand off to post-game
        animateSolve(container, state.tier, () => {
          if (typeof callbacks.onComplete === 'function') {
            callbacks.onComplete(state);
          }
        });
      } else {
        if (typeof callbacks.onComplete === 'function') {
          callbacks.onComplete(state);
        }
      }
      return;
    }
    focusElement(container);
  }

  function updateStatus() {
    // 2.14.29: a puzzle ended by "Show answers" is over; it is not judged again here
    // (some of its words may be the player's own, which would read as solved).
    if (state.status !== 'in-progress') return;
    const allFilled = state.slots_filled.every((s) => s);
    const allRevealed = state.slot_revealed.every((r) => r);

    if (allFilled) {
      state.status = allRevealed ? 'failed' : 'solved';
      state.finished_at_ms = Date.now();
      return;
    }
    // 2.14.29: the hints running out no longer ends the puzzle. It went on to fill in
    // every open word and mark the day failed the moment the last hint was pressed;
    // that is now the player's own choice, "Show answers" (showAnswers below).
  }

  // ---- Show answers (2.14.29) ----
  // What the box says on its second tap. A walked-to puzzle is not today's.
  function endWords() {
    return testMode ? 'to end this puzzle' : 'to end today\u2019s puzzle';
  }
  function disarmEnd() {
    if (endArmTimer) { clearTimeout(endArmTimer); endArmTimer = null; }
    if (!endArmed) return false;
    endArmed = false;
    return true;
  }
  // The first tap asks; the second, within six seconds and with no play between, ends
  // the puzzle exactly as the last hint used to: every word still open is filled in and
  // marked as given, and the day is not solved.
  //
  // NOT IN ONE BURST. The box sits where the Letter box sat, and a player used to four
  // hints presses Letter two or three times running: the first press is now the hint,
  // and the next two would have asked and then ended the day (found by the independent
  // check of this build). So the box takes no press for END_QUIET_MS after it appears,
  // and the second press counts only END_GAP_MS after the first. The clock is the
  // page's own running time, which no change of date can stop.
  function showAnswers() {
    touchActivity(state);
    if (state.status !== 'in-progress' || state.stars_remaining > 0) return;
    const now = tick();
    if (!endArmed) {
      if (now - endShownAt < END_QUIET_MS) return;
      endArmed = true;
      endArmedAt = now;
      endArmTimer = setTimeout(() => { if (disarmEnd()) renderHintsBar(); }, 6000);
      // The box is redrawn. A keyboard player stays on it for the second press; anyone
      // else goes back to the board, so typing still reaches the game.
      const onBox = tabbing && hintsBar.contains(document.activeElement);
      renderHintsBar(onBox);
      if (!onBox) focusBoard();
      liveRegion.announce('Tap again ' + endWords() + '.');
      return;
    }
    if (now - endArmedAt < END_GAP_MS) return;
    disarmEnd();
    for (let i = 0; i < state.par; i++) {
      if (!state.slots_filled[i]) {
        const canon = answerForSlot(i);
        state.slots_filled[i] = canon || '';
        state.slot_revealed[i] = true;
        state.reveals_used = (Number(state.reveals_used) || 0) + 1;
      }
    }
    state.current_input = '';
    state.status = 'failed';
    state.finished_at_ms = Date.now();
    afterStateChange();
  }

  // ---- Cleanup hook ----

  function destroy() {
    container.removeEventListener('keydown', onKeyDown);
    document.removeEventListener('keydown', onTabKey, true);
    document.removeEventListener('pointerdown', onPointer, true);
    disarmEnd();
    if (endQuietTimer) { clearTimeout(endQuietTimer); endQuietTimer = null; }
    if (toastTimer) clearTimeout(toastTimer);
    if (keyboard && typeof keyboard.teardown === 'function') keyboard.teardown();
  }

  // Kick off.
  render();
  // Focus the container so physical keyboard works immediately.
  setTimeout(() => focusElement(container), 0);
  // If we resumed mid-puzzle, replay a short announcement so screen
  // readers know where we are.
  if (state.status === 'in-progress' &&
      (state.slots_filled.some(Boolean) ||
       state.letters_revealed.some((n) => n > 0))) {
    liveRegion.announce('Resumed in progress. ' +
      state.slots_filled.filter(Boolean).length + ' of ' + state.par +
      ' bridges solved.');
  }

  return {
    state,
    destroy,
    saveState: () => saveState(state),
  };
}

// ---- Coloring helpers ----
//
// colorWord assigns each guessed letter green / yellow / absent against a
// single target word, Wordle-style (greens first, then yellows honoring
// letter multiplicity). chooseColorTarget picks the valid bridge word the
// player's guesses collectively point at — so on a slot that accepts more
// than one word, the colors follow whichever one the player is closest to.
// Ties lean toward the canonical answer (the word hints reveal).

function colorWord(guess, target) {
  const n = guess.length;
  const res = new Array(n).fill('absent');
  if (!target) return res;
  const counts = {};
  for (const ch of target) counts[ch] = (counts[ch] || 0) + 1;
  const m = Math.min(n, target.length);
  for (let i = 0; i < m; i++) {
    if (guess[i] === target[i]) { res[i] = 'green'; counts[guess[i]] -= 1; }
  }
  for (let i = 0; i < n; i++) {
    if (res[i] === 'green') continue;
    const c = guess[i];
    if (counts[c] > 0) { res[i] = 'yellow'; counts[c] -= 1; }
  }
  return res;
}

function chooseColorTarget(guesses, validWords, canonical) {
  if (!validWords || validWords.length === 0) return canonical || null;
  if (validWords.length === 1) return validWords[0];
  let best = null;
  let bestScore = -1;
  for (const w of validWords) {
    let s = 0;
    for (const g of guesses) {
      const col = colorWord(g, w);
      for (const c of col) { if (c === 'green') s += 3; else if (c === 'yellow') s += 1; }
    }
    if (s > bestScore || (s === bestScore && w === canonical)) { bestScore = s; best = w; }
  }
  return best;
}

// ---- Hint helpers ----
//
// We don't have the canonical answer from the server. Instead we
// compute "a canonical" — one valid completion of the current state —
// by walking the graph from `prev` to `end_word`. Since 2.14.23 the
// server's design (lengths + fingerprints) picks the designed word out
// of those; without one, the shortest, then alphabetical, as before.
//
// This is cached on the state object under `_hint_cache[slotIdx]` so
// repeated lookups for the same slot don't repeat the walk.

function canonicalForSlot(state, slotIdx) {
  if (!state._hint_cache) state._hint_cache = {};
  const cached = state._hint_cache[slotIdx];
  if (cached) return cached;

  // For inactive future slots, project forward through the canonical chain
  // so every slot shows its letter count up front (not just the active one).
  let prev;
  if (slotIdx === 0) {
    prev = state.start_word;
  } else if (state.slots_filled[slotIdx - 1]) {
    prev = state.slots_filled[slotIdx - 1];
  } else {
    prev = canonicalForSlot(state, slotIdx - 1);
  }
  if (!prev) return null;

  const slotsAfter = state.par - 1 - slotIdx;
  let candidates = validBridgeWords(prev, state.end_word, slotsAfter);
  candidates.sort((a, b) => a.length - b.length || a.localeCompare(b));
  // PLAY THE PUZZLE AS IT WAS BUILT (2.14.23). The shortest-then-alphabetical
  // pick above is what sized the boxes and aimed the hints until now, and on 32
  // of the 197 dailies it was not the word the puzzle was built on (GOOD → ? → ?
  // → HOUSE built as NIGHT CLUB showed three boxes and wanted MAN). The server
  // sends each designed word's length and fingerprint: the designed word wins
  // when it still fits here; failing that (the player locked a different word
  // earlier) a word of the designed length; and only then the old pick.
  const d = state._design;
  if (d) {
    const designed = candidates.find((w) => designFingerprint(state.start_word, state.end_word, slotIdx, w) === d.fp[slotIdx]);
    if (designed) {
      candidates = [designed];
    } else {
      const sameLen = candidates.filter((w) => w.length === d.lens[slotIdx]);
      if (sameLen.length) candidates = sameLen;
    }
  }
  const choice = candidates[0] || null;
  state._hint_cache[slotIdx] = choice;
  return choice;
}

// FNV-1a 32-bit of "start>end#slot:word", 8 hex digits. The same function as
// FLOW_FHT_Module::design_fingerprint() on the server.
export function designFingerprint(start, end, slot, word) {
  const s = String(start).toLowerCase() + '>' + String(end).toLowerCase() + '#' + slot + ':' + String(word).toLowerCase();
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return ('00000000' + h.toString(16)).slice(-8);
}

function validDesign(d, par) {
  if (!d || !Array.isArray(d.lens) || !Array.isArray(d.fp)) return null;
  if (d.lens.length !== par || d.fp.length !== par) return null;
  return { lens: d.lens.map(Number), fp: d.fp.map(String) };
}

function currentHintLengthForSlot(state, slotIdx) {
  // A slot that has committed to an answer is that answer's length — a game
  // saved before 2.14.23 may have committed to a word of the old length.
  const committed = state.slot_answer && state.slot_answer[slotIdx];
  if (committed) return committed.length;
  const c = canonicalForSlot(state, slotIdx);
  return c ? c.length : null;
}

function firstEmptySlot(state) {
  for (let i = 0; i < state.par; i++) {
    if (!state.slots_filled[i]) return i;
  }
  return state.par - 1;
}

/**
 * Clear the saved state for a puzzle. Used when the player explicitly
 * "plays again" or when the server returns a different version.
 *
 * testMode picks which prefix to wipe.
 */
export function clearSavedState(puzzleId, testMode) {
  storageRemove(stateKey(puzzleId, !!testMode));
}
